import { Link } from 'react-router-dom';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-800 mb-2">
            🌸 Spring Lotus
          </h1>
          <p className="text-2xl font-semibold text-purple-600 mb-4">
            Reflect. Reframe. Renew.
          </p>
          <p className="text-xl text-gray-600 mb-8">
            A simple, non-medical tool to reflect on your feelings and receive supportive guidance.
          </p>
          <Link
            to="/check-in"
            className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-4 px-8 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all text-lg shadow-lg"
          >
            Start Your Check-In →
          </Link>
        </div>

        {/* 5 Whys Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            🤔 Why This Matters: The 5 Whys
          </h2>

          <div className="space-y-6">
            {/* Why 1 */}
            <div className="border-l-4 border-purple-500 pl-6 py-2">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                1. Why Daily Reflection Matters
              </h3>
              <p className="text-gray-700">
                Most people rush through their days without pausing to understand their emotions. Daily reflection builds emotional awareness—the foundation of well-being. When you know what you're feeling and why, you can respond thoughtfully instead of reacting impulsively.
              </p>
            </div>

            {/* Why 2 */}
            <div className="border-l-4 border-blue-500 pl-6 py-2">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                2. Why People Avoid Reflection
              </h3>
              <p className="text-gray-700">
                Sitting with difficult emotions feels uncomfortable. Journaling feels like homework. Therapy feels formal and expensive. People need something quick, accessible, and judgment-free—a tool that meets them where they are, not where they "should" be.
              </p>
            </div>

            {/* Why 3 */}
            <div className="border-l-4 border-pink-500 pl-6 py-2">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                3. Why Existing Tools Feel Clinical
              </h3>
              <p className="text-gray-700">
                Many wellness apps use medical language, ask invasive questions, or feel like they're diagnosing you. This creates anxiety instead of relief. People want warmth, not clinical detachment. They want to be heard, not analyzed.
              </p>
            </div>

            {/* Why 4 */}
            <div className="border-l-4 border-green-500 pl-6 py-2">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                4. Why Simple AI Support Helps
              </h3>
              <p className="text-gray-700">
                An empathetic AI response validates your feelings instantly. It offers perspective without judgment. It suggests small, doable actions. It's available 24/7. For many, this bridge between isolation and professional help is exactly what they need.
              </p>
            </div>

            {/* Why 5 */}
            <div className="border-l-4 border-orange-500 pl-6 py-2">
              <h3 className="text-xl font-bold text-gray-800 mb-2">
                5. Why Non-Medical Positioning Is Important
              </h3>
              <p className="text-gray-700">
                This tool is not therapy. It's not diagnosis. It's emotional awareness. By being clear about what we are—a supportive companion—we remove barriers to use. People feel safe exploring their feelings without fear of being labeled or pathologized.
              </p>
            </div>
          </div>
        </div>

        {/* Features Preview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl p-6 shadow-md">
            <span className="text-4xl mb-4 block">💭</span>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Share Your Mood</h3>
            <p className="text-gray-600">Express how you're feeling in a simple, judgment-free way.</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md">
            <span className="text-4xl mb-4 block">💬</span>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Get Support</h3>
            <p className="text-gray-600">Receive empathetic responses and practical coping suggestions.</p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md">
            <span className="text-4xl mb-4 block">📊</span>
            <h3 className="text-lg font-bold text-gray-800 mb-2">Track Patterns</h3>
            <p className="text-gray-600">See your mood trends and stress levels over time.</p>
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-indigo-100 to-purple-100 rounded-2xl p-8 text-center border-2 border-indigo-300">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">Ready to Start?</h3>
          <p className="text-gray-700 mb-6">
            Take 2 minutes to check in with yourself. No judgment. Just support.
          </p>
          <Link
            to="/check-in"
            className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-3 px-8 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all"
          >
            Start Check-In
          </Link>
        </div>
      </div>
    </div>
  );
}
